import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Switch,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import type { NotificationChannel } from '../../models';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingBackdrop from '../../components/LoadingBackdrop';
import { useSnackbar } from 'notistack';
import {
  useGetNotificationChannelsQuery,
  useDeleteNotificationChannelMutation,
  useUpdateNotificationChannelMutation
} from '../../services/connectApi';

const ChannelsList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedChannel, setSelectedChannel] = useState<NotificationChannel | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { data, isLoading, refetch } = useGetNotificationChannelsQuery({ page: page + 1, page_size: pageSize });
  const channels = data?.results || [];
  React.useEffect(() => {
    if (data?.count !== undefined) setTotalCount(data.count);
  }, [data]);

  const [deleteChannel, { isLoading: isDeleting }] = useDeleteNotificationChannelMutation();
  const [updateChannel, { isLoading: isUpdating }] = useUpdateNotificationChannelMutation();

  const handleEdit = (id: string) => {
    navigate(`/connect/channels/${id}`);
  };

  const handleDeleteClick = (channel: NotificationChannel) => {
    setSelectedChannel(channel);
    setDeleteConfirmOpen(true);
  };

  const handleToggleActive = async (channel: NotificationChannel) => {
    try {
      await updateChannel({
        id: channel.id,
        data: { is_active: !channel.is_active }
      }).unwrap();
      
      enqueueSnackbar(
        `Channel ${channel.is_active ? 'deactivated' : 'activated'} successfully`, 
        { variant: 'success' }
      );
      
      refetch();
    } catch (error) {
      enqueueSnackbar('Failed to update channel status', { variant: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedChannel) {
      try {
        await deleteChannel(selectedChannel.id).unwrap();
        enqueueSnackbar('Channel deleted successfully', { variant: 'success' });
        refetch();
      } catch (error) {
        enqueueSnackbar('Failed to delete channel', { variant: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
  };

  const getChannelTypeLabel = (type: string): string => {
    switch (type) {
      case 'push':
        return 'Push Notification';
      case 'email':
        return 'Email';
      case 'sms':
        return 'SMS';
      case 'whatsapp':
        return 'WhatsApp';
      default:
        return type;
    }
  };

  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1,
      minWidth: 180 
    },
    { 
      field: 'type', 
      headerName: 'Type', 
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<NotificationChannel, any>) => (
        <Chip 
          label={getChannelTypeLabel(params.value)} 
          color={
            params.value === 'push' ? 'primary' :
            params.value === 'email' ? 'secondary' :
            params.value === 'sms' ? 'success' :
            params.value === 'whatsapp' ? 'info' :
            'default'
          }
          size="small"
        />
      ) 
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 1,
      minWidth: 250 
    },
    {
      field: 'is_active',
      headerName: 'Active',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<NotificationChannel, any>) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleActive(params.row)}
          color="primary"
        />
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      minWidth: 180,
      valueFormatter: (params: { value: string | null }) => {
        return params.value ? format(new Date(params.value), 'MMM d, yyyy HH:mm') : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<NotificationChannel, any>) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(params.row.id)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(params.row)} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Notification Channels
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure channels for sending notifications (push, email, SMS, WhatsApp)
      </Typography>
      
      <Paper elevation={2} sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={channels}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={isLoading}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'updated_at', sort: 'desc' }],
            },
          }}
        />
      </Paper>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Channel"
        content={`Are you sure you want to delete the channel "${selectedChannel?.name}"? This action cannot be undone.`}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <LoadingBackdrop open={isDeleting || isUpdating} />
    </Box>
  );
};

export default ChannelsList;
